import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import { drug_models } from '@/models/modelData/DrugModels';
import { calculateSince, newDosesOnly } from '@/components/services/common';
import { DynamicSimulation } from '@/models/DynamicSimulation';
import { Patient } from '@/models/Patient';

function makeSimulation(state: any) {
  return new DynamicSimulation({
    patient: state.patient,
    modelId: state.modelId,
    pathology: state.pathology,
    doses: state.doses,
    target: state.target,
    drugId: state.drugId,
    delivery: state.delivery,
  });
}

export const useDosingStore = defineStore('dosing', {
  state: () => ({
    drugId: null as string | null,
    modelId: null as string | null,
    patient: null as any,
    delivery: null as any,
    target: null as any,
    pathology: null as any,
    doses: [] as any[],
    simulation: new DynamicSimulation({
      patient: null,
      pathology: null,
      doses: [],
      target: null,
      delivery: null,
    }),
    selectedId: null as string | null,
    selectedPlan: null as any,
    loading: false,
    auth: undefined as any,
  }),

  getters: {
    getDrug: (state): any => {
      if (state.drugId) {
        return drug_models.find((d: any) => d.id === state.drugId) ?? null;
      } else if (state.modelId) {
        return (
          drug_models.find((d: any) => d.models.find((m: any) => m.code === state.modelId)) ?? null
        );
      }
      return null;
    },
    getModel: (state): any => {
      if (!state.modelId) return null;
      for (const d of drug_models as any[]) {
        const found = d.models.find((m: any) => m.code === state.modelId);
        if (found) return found;
      }
      return null;
    },
    getPatient: (state) => state.patient,
    getDelivery: (state) => state.delivery,
    getTarget: (state) => state.target,
    getPathology: (state) => state.pathology,
    getDoses: (state) => state.doses,
    getSimulation: (state) => state.simulation,
    getSelectedId: (state) => state.selectedId,
    getLoading: (state) => state.loading,
    getMember: (state) => state.auth,
    getSelectedPlan: (state) => state.selectedPlan,
    isReady: (state): boolean => {
      if (state.simulation) {
        return state.simulation.isReady();
      }
      return false;
    },
  },

  actions: {
    setDrug(drug: any) {
      this.drugId = drug?.id ?? null;
      this.simulation = new DynamicSimulation({
        patient: this.patient,
        modelId: this.modelId,
        pathology: this.pathology,
        doses: this.doses,
        target: this.target,
        drug,
        delivery: this.delivery,
      });
    },
    setDrugId(drugId: string | null) {
      this.drugId = drugId;
      this.simulation = makeSimulation(this);
      this.selectedPlan = null;
    },
    setSelectedModel(modelId: string | null) {
      this.modelId = modelId;
      this.simulation = makeSimulation(this);
      this.selectedPlan = null;
    },
    setPatient(patient: any) {
      this.patient = patient ? new Patient({ ...patient }) : null;
      this.simulation = makeSimulation(this);
      this.selectedPlan = null;
    },
    setDelivery(item: any) {
      this.delivery = item ? { ...item } : null;
      this.simulation = makeSimulation(this);
      this.selectedPlan = null;
    },
    setTarget(item: any) {
      // Normalize legacy 'AUC/MIC' target type to 'AUC' for backward compatibility
      if (item?.target === 'AUC/MIC') {
        item = { ...item, target: 'AUC' };
      }
      this.target = item ? { ...item } : null;
      this.simulation = makeSimulation(this);
      this.selectedPlan = null;
    },
    setPathology(item: any) {
      this.pathology = item ? { ...item } : null;
      this.simulation = makeSimulation(this);
      this.selectedPlan = null;
    },
    setDoses(items: any[]) {
      this.doses = [...items];
      if (this.doses.length > 1) {
        this.doses = calculateSince([...this.doses]);
      }
      this.simulation = makeSimulation(this);
      this.selectedPlan = null;
    },
    clearDoses() {
      this.doses = [];
      this.simulation = makeSimulation(this);
      this.selectedPlan = null;
    },
    // Adds the entries that duplicate neither an existing entry nor each other (same type
    // and minute). Existing entries are never removed. Returns how many were added/skipped.
    addDose(item: any): { added: number; skipped: number } {
      // A single entry is copied (keeping its prototype, so Dose methods still work)
      const incoming =
        item.length !== undefined
          ? [...item]
          : [Object.assign(Object.create(Object.getPrototypeOf(item)), item)];
      const added = newDosesOnly(this.doses, incoming);
      if (added.length > 0) this.setDoses([...this.doses, ...added]);
      return { added: added.length, skipped: incoming.length - added.length };
    },
    // Replaces an entry with an updated copy (same prototype and id) instead of changing the
    // store's object in place. Returns the store's new entry.
    updateDose(dose: any, changes: Record<string, any>) {
      const raw = toRaw(dose);
      const index = this.doses.findIndex((d: any) => toRaw(d) === raw);
      if (index < 0) return null;
      const copy = Object.assign(Object.create(Object.getPrototypeOf(raw)), raw, changes);
      const doses = [...this.doses];
      doses[index] = copy;
      this.setDoses(doses);
      return this.doses.find((d: any) => toRaw(d) === copy) ?? null;
    },
    removeDose(item: any) {
      // By identity: ids can repeat (series ids, saved patients vs the Dose counter)
      this.doses = [...this.doses.filter((d: any) => toRaw(d) !== toRaw(item))];
      if (this.doses.length > 1) {
        this.doses = calculateSince([...this.doses]);
      }
      this.simulation = makeSimulation(this);
      this.selectedPlan = null;
    },
    setSimulation(item: any) {
      this.simulation = { ...item };
      this.selectedPlan = null;
    },
    clearSimulation() {
      this.drugId = null;
      this.modelId = null;
      this.patient = null;
      this.delivery = null;
      this.target = null;
      this.pathology = null;
      this.doses = [];
      this.selectedId = null;
      this.simulation = new DynamicSimulation({
        patient: null,
        pathology: null,
        doses: [],
        target: null,
        delivery: null,
      });
      this.selectedPlan = null;
    },
    clearDrug() {
      this.drugId = null;
      this.modelId = null;
      this.simulation = makeSimulation(this);
      this.selectedPlan = null;
    },
    clearModel() {
      this.modelId = null;
      this.simulation = makeSimulation(this);
      this.selectedPlan = null;
    },
    clearAll() {
      this.$reset();
    },
    setLoading(value: boolean) {
      this.loading = value;
    },
    setAuth(member: any) {
      this.auth = member;
    },
    setSelectedId(id: string | null) {
      this.selectedId = id;
    },
    setSelectedPlan(plan: any) {
      this.selectedPlan = plan;
    },
    clearSelectedPlan() {
      this.selectedPlan = null;
    },

    // Atomic drug switch: sets drugId before clearing model/patient so the
    // selectedModel watcher in DrugSelect fires after drugId is already updated.
    switchDrug(drug: any) {
      this.$patch((state) => {
        state.drugId = drug?.id ?? null;
        state.modelId = null;
        state.patient = null;
        state.delivery = null;
        state.target = null;
        state.pathology = null;
        state.doses = [];
        state.selectedId = null;
        state.selectedPlan = null;
      });
      this.simulation = new DynamicSimulation({
        patient: null,
        pathology: null,
        doses: [],
        target: null,
        delivery: null,
      });
    },

    // Composite actions (replaces Vuex actions that called multiple commits)
    saveDrug(drug: any) {
      this.setDrug(drug);
    },
    saveModel(modelId: string | null) {
      this.setSelectedModel(modelId);
    },
    savePatient(patient: any) {
      this.setPatient(patient);
    },
    saveDelivery(item: any) {
      this.setDelivery(item);
    },
    saveTarget(item: any) {
      this.setTarget(item);
    },
    savePathology(item: any) {
      this.setPathology(item);
    },
    saveDoses(items: any[]) {
      this.setDoses(items);
    },
    saveSimulation(item: any) {
      this.setSimulation(item);
      this.loading = false;
    },
    saveSelectedId(id: string | null) {
      this.setSelectedId(id);
    },
    saveSelectedPlan(plan: any) {
      this.setSelectedPlan(plan);
    },
    saveLoading(value: boolean) {
      this.loading = value;
    },
    login(member: any) {
      this.auth = member;
    },
    logout() {
      this.auth = undefined;
    },
    loadPatientData(patient: any) {
      console.log('Load patient from ', patient);

      // Resolve drugId from modelId
      let drugId: string | null = null;
      if (patient.modelId) {
        (drug_models as any[]).forEach((d) => {
          const found = d.models.find((i: any) => i.code === patient.modelId);
          if (found) drugId = d.id;
        });
      }

      // Normalise target (legacy 'AUC/MIC' → 'AUC')
      let target = patient.target;
      if (target?.target === 'AUC/MIC') {
        target = { ...target, target: 'AUC' };
      }

      // Normalise doses
      let doses = [...(patient.doses ?? [])];
      if (doses.length > 1) {
        doses = calculateSince([...doses]);
      }

      // Batch all state changes in one reactive update to avoid multiple re-renders
      // (calling individual setters from an async callback triggers a tooltip directive
      // update on every assignment, causing bootstrap-vue-next to log a vnode warning)
      this.$patch((state) => {
        state.selectedId = patient.id ?? this.selectedId;
        state.drugId = drugId;
        state.modelId = patient.modelId ?? null;
        state.patient = patient.patient ? new Patient({ ...patient.patient }) : null;
        state.delivery = patient.delivery
          ? { ...patient.delivery }
          : { daysToDisplay: 3, applyInterindividualVariability: true };
        state.target = target ? { ...target } : null;
        state.pathology = patient.pathology ? { ...patient.pathology } : null;
        state.doses = doses;
        state.selectedPlan = null;
      });

      // Rebuild simulation once from final state
      this.simulation = makeSimulation(this);
      console.log('loaded simulation ', this.simulation);
    },
  },
});
